// Top-level entry point for the in-app builder.
//
// The list lives inline on the page; opening an app launches the builder in a
// full-screen modal dialog so it covers the whole viewport (including the
// dashboard chrome).

import React from "react";
import { Dialog } from "@mui/material";
import AppsList from "./AppsList";
import BuilderEditor from "./BuilderEditor";
import { createBuilderApi } from "./api";

interface Props {
  getToken: () => Promise<string>;
  companyId: string;
  currentUser?: { uid?: string } | null;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
  // Seed value from the parent's /usage response. Threading it down avoids a
  // second /usage round-trip and lets the "In menu" chip render with the
  // first paint instead of after a network blip.
  initialMenuAppIds?: string[];
}

// Stable across renders so MUI's Dialog never sees a fresh PaperProps identity
// while the AI is streaming. A fresh object every render can trigger MUI to
// re-evaluate its Paper styles and was the suspected cause of a per-render
// flicker that briefly exposed the page behind the fullscreen dialog.
const DIALOG_PAPER_PROPS = {
  sx: {
    bgcolor: "background.default",
    backgroundImage: "none",
    opacity: 1,
  },
} as const;

const AppBuilder: React.FC<Props> = ({
  getToken,
  companyId,
  currentUser,
  openDialog,
  initialMenuAppIds,
}) => {
  const api = React.useMemo(
    () => createBuilderApi(getToken, companyId),
    [getToken, companyId],
  );
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [seedPrompt, setSeedPrompt] = React.useState<string | undefined>(undefined);
  const [menuAppIds, setMenuAppIds] = React.useState<string[]>(
    initialMenuAppIds || [],
  );

  // If the parent's value arrives later (e.g. /usage finishes after this
  // component mounts) sync it in. Skipped once the user has made a local
  // edit — `updateMenuAppIds` writes to the backend and uses the canonical
  // list it returns, which we don't want to clobber with a stale prop.
  const userTouchedRef = React.useRef(false);
  React.useEffect(() => {
    if (userTouchedRef.current) return;
    if (initialMenuAppIds) setMenuAppIds(initialMenuAppIds);
  }, [initialMenuAppIds]);

  const updateMenuAppIds = React.useCallback(
    async (next: string[]) => {
      // Optimistic update; backend filters out inaccessible ids and returns
      // the canonical list, which we then sync into local state.
      userTouchedRef.current = true;
      setMenuAppIds(next);
      try {
        const persisted = await api.setMenuAppIds(next);
        setMenuAppIds(persisted);
      } catch {
        // Revert on failure.
        setMenuAppIds((prev) => prev);
      }
    },
    [api],
  );

  return (
    <>
      <AppsList
        api={api}
        companyId={companyId}
        getToken={getToken}
        onOpen={(id, prompt) => {
          setSeedPrompt(prompt);
          setEditingId(id);
        }}
        openDialog={openDialog}
        currentUid={currentUser?.uid}
        menuAppIds={menuAppIds}
        onMenuAppIdsChange={updateMenuAppIds}
      />
      <Dialog
        open={!!editingId}
        onClose={() => {
          setEditingId(null);
          setSeedPrompt(undefined);
        }}
        fullScreen
        // The fullScreen Paper covers the whole viewport, so the backdrop is
        // never visible. Skipping it removes the global blur(4px) backdrop
        // filter, which otherwise keeps re-compositing while the AI streams
        // and shows through as a flicker of the dashboard chrome behind.
        hideBackdrop
        PaperProps={DIALOG_PAPER_PROPS}
      >
        {editingId && (
          <BuilderEditor
            api={api}
            appId={editingId}
            companyId={companyId}
            getToken={getToken}
            onClose={() => {
              setEditingId(null);
              setSeedPrompt(undefined);
            }}
            openDialog={openDialog}
            currentUid={currentUser?.uid}
            menuAppIds={menuAppIds}
            onMenuAppIdsChange={updateMenuAppIds}
            initialPrompt={seedPrompt}
          />
        )}
      </Dialog>
    </>
  );
};

export default AppBuilder;
